/** ISO-неделя (UTC): единый контракт календарной позиции training_week. */

export type IsoWeekPair = { isoYear: number; isoWeek: number };

export function normalizeIsoYear(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const year = Number(value);
  if (!Number.isInteger(year) || year < 1970 || year > 2100) return null;
  return year;
}

export function normalizeIsoWeekNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const week = Number(value);
  if (!Number.isInteger(week) || week < 1 || week > 53) return null;
  return week;
}

export function parseIsoFromWeek(week: {
  isoYear?: number | string | null;
  isoWeek?: number | string | null;
  iso_year?: number | string | null;
  iso_week?: number | string | null;
} | null | undefined): IsoWeekPair | null {
  if (!week || typeof week !== "object") {
    return null;
  }
  const isoYear = normalizeIsoYear(week.isoYear ?? week.iso_year);
  const isoWeek = normalizeIsoWeekNumber(week.isoWeek ?? week.iso_week);
  if (isoYear === null || isoWeek === null) return null;
  return { isoYear, isoWeek };
}

export function parseIsoFromCalendarRow(row: {
  isoYear?: number | string | null;
  isoWeek?: number | string | null;
} | null | undefined): IsoWeekPair | null {
  return parseIsoFromWeek(row ?? undefined);
}

export function isoWeekStartDate(isoYear: number, isoWeek: number): string {
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const jan4Weekday = jan4.getUTCDay() || 7;
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - (jan4Weekday - 1) + (isoWeek - 1) * 7);
  return monday.toISOString().slice(0, 10);
}

export function isoWeekEndDate(isoYear: number, isoWeek: number): string {
  const start = isoWeekStartDate(isoYear, isoWeek);
  const end = new Date(`${start}T00:00:00.000Z`);
  end.setUTCDate(end.getUTCDate() + 6);
  return end.toISOString().slice(0, 10);
}

export function weekDateBounds(isoYear: number, isoWeek: number): {
  dateStart: string;
  dateEnd: string;
} {
  return {
    dateStart: isoWeekStartDate(isoYear, isoWeek),
    dateEnd: isoWeekEndDate(isoYear, isoWeek),
  };
}

export function formatIsoLabel(isoYear: number | null | undefined, isoWeek: number | null | undefined): string {
  const y = normalizeIsoYear(isoYear);
  const w = normalizeIsoWeekNumber(isoWeek);
  if (y === null || w === null) return "";
  return `${y}#${String(w).padStart(2, "0")}`;
}

export function getIsoWeekInfo(dateStart: string): IsoWeekPair & { dateEnd: string } | null {
  const normalized = normalizeDateStartInput(dateStart);
  if (!normalized) return null;

  const date = new Date(`${normalized}T12:00:00.000Z`);
  const weekday = date.getUTCDay() || 7;
  const thursday = new Date(date);
  thursday.setUTCDate(date.getUTCDate() + 4 - weekday);
  const isoYear = thursday.getUTCFullYear();

  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const jan4Weekday = jan4.getUTCDay() || 7;
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - (jan4Weekday - 1));
  const isoWeek = Math.floor((thursday.getTime() - week1Monday.getTime()) / (7 * 86400000)) + 1;

  const bounds = weekDateBounds(isoYear, isoWeek);
  return { isoYear, isoWeek, dateEnd: bounds.dateEnd };
}

export function nextIsoWeek(isoYear: number, isoWeek: number): IsoWeekPair {
  const bounds = weekDateBounds(isoYear, isoWeek);
  const nextMonday = new Date(`${bounds.dateStart}T00:00:00.000Z`);
  nextMonday.setUTCDate(nextMonday.getUTCDate() + 7);
  const info = getIsoWeekInfo(nextMonday.toISOString().slice(0, 10));
  return info ?? { isoYear, isoWeek: Math.min(isoWeek + 1, 53) };
}

export function normalizeDateStartInput(value: unknown): string {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : "";
}

export function suggestTrainingDate(iso: IsoWeekPair | null, dayOffset = 0): string | null {
  if (!iso) return null;
  const start = isoWeekStartDate(iso.isoYear, iso.isoWeek);
  const dt = new Date(`${start}T00:00:00.000Z`);
  dt.setUTCDate(dt.getUTCDate() + Math.max(0, dayOffset));
  return dt.toISOString().slice(0, 10);
}
